<template>
  <!-- Flat: the panel opens inside the property record's outlined-card defaults,
       which drew a border around the whole stepper. -->
  <v-card variant="flat">
    <v-stepper v-model="e1" class="elevation-0">
      <!-- Vuetify shades the header with a drop shadow, a dark band on the dark
           panel; a divider separates it like the panel's other sections. -->
      <v-stepper-header class="elevation-0">
        <v-stepper-item
          v-if="sourceType !== 'newContact' && sourceType !== 'ContactSelect'"
          editable
          :complete="e1 > 1"
          :color="stepColor(1, e1 > 1)"
          :value="1"
          title="Search for a contact"
        />
        <v-divider />
        <v-stepper-item
          v-if="sourceType !== 'company' && !isContributorSource"
          editable
          :complete="e1 > stepTwo"
          :color="stepColor(stepTwo, e1 > stepTwo)"
          :value="stepTwo"
          title="Search for a company"
        />
        <v-divider v-if="sourceType !== 'company' && !isContributorSource" />
        <v-stepper-item
          v-if="!isContributorSource"
          editable
          :complete="e1 > stepThree"
          :color="stepColor(stepThree, e1 > stepThree)"
          :value="stepThree"
          title="Enter Contact Information"
        />
        <v-divider v-if="!isContributorSource" />
      </v-stepper-header>
      <v-divider />
      <!-- Steps inset 16px like the other side panels (Edit Property): no stepper
           window margin, flat cards, one 16px padding. -->
      <v-stepper-window class="ma-0">
        <v-stepper-window-item
          v-if="sourceType !== 'newContact' && sourceType !== 'ContactSelect'"
          :value="1"
        >
          <v-card variant="flat">
            <v-container class="pa-4">
              <v-form ref="form">
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      ref="searchField"
                      v-model="contactKeyword"
                      label="Search by name or email"
                      prepend-inner-icon="mdi-magnify"
                      clearable
                      :hint="isContributorSource ? 'At least 3 characters. Only contacts linked to an active member are listed.' : 'At least 3 characters'"
                      :persistent-hint="isContributorSource"
                      :loading="loadingContacts"
                      :rules="[rules.required, rules.min]"
                      autocomplete="off"
                    />
                  </v-col>
                </v-row>
                <v-row v-if="hasKeyword && loadingContacts" class="mt-0">
                  <v-col cols="12" class="pt-3">
                    <v-card variant="outlined" data-test="contact-search-skeleton">
                      <v-skeleton-loader type="list-item-avatar-three-line@5" />
                    </v-card>
                  </v-col>
                </v-row>
                <v-row v-if="contacts.length && hasKeyword && !loadingContacts" class="mt-0">
                  <v-col cols="12" class="pt-3">
                    <div class="d-flex align-center text-body-2 text-medium-emphasis mb-2">
                      Results ({{ contacts.length }} contacts)
                      <v-spacer />
                      <span v-if="selected.length" class="text-primary">{{ selected.length }} selected</span>
                    </div>
                    <v-card variant="outlined">
                      <v-list lines="three" class="py-0">
                        <template v-for="(tempContact, index) in paginatedContacts" :key="tempContact._key">
                          <v-divider v-if="index" />
                          <v-list-item
                            ripple
                            :active="selected.includes(tempContact._key)"
                            color="primary"
                            @click="toggleContact(tempContact._key)"
                          >
                            <!-- The avatar stays a direct child of the prepend slot so Vuetify
                                 spaces it off the title. -->
                            <template #prepend>
                              <v-list-item-action start>
                                <v-checkbox-btn
                                  :model-value="selected.some(item => item === tempContact._key)"
                                  @click.stop="toggleContact(tempContact._key)"
                                />
                              </v-list-item-action>
                              <v-avatar>
                                <img :src="tempContact.img || contactPlaceholder" alt="">
                              </v-avatar>
                            </template>

                            <div>
                              <v-list-item-title>
                                {{ tempContact.first_name }}
                                {{ tempContact.last_name }} <span v-if="tempContact.nickname">({{ tempContact.nickname }})</span>
                                <ClientAccessChip v-if="tempContact.is_client" class="ml-1" />
                              </v-list-item-title>
                              <v-list-item-subtitle v-if="tempContact.company">
                                {{ tempContact.company.name }}
                              </v-list-item-subtitle>
                              <v-list-item-subtitle>
                                {{ tempContact.title }}
                              </v-list-item-subtitle>
                            </div>
                          </v-list-item>
                        </template>
                      </v-list>
                      <template v-if="contactsPaginationLength > 1">
                        <v-divider />
                        <v-pagination
                          v-model="currentContactsPage"
                          :length="contactsPaginationLength"
                          :total-visible="paginationTotalVisible"
                          density="comfortable"
                          size="small"
                          class="py-1"
                        />
                      </template>
                    </v-card>
                    <div v-if="!selected.length && !isContributorSource" class="text-body-2 mt-3">
                      Can't find the contact you're looking for?
                      <a data-track="contact.wizard.create" @click="e1 = 2">Create a new contact</a>
                    </div>
                  </v-col>
                  <v-col v-if="roleAlert" cols="12">
                    <v-alert id="roleAlert" color="red" class="text-white">
                      {{ roleMessage }}
                    </v-alert>
                  </v-col>
                  <v-col v-if="isShowContactRoles" cols="12">
                    <v-select
                      v-model="role"
                      :items="contactRoles"
                      label="Contact Role"
                      :rules="[rules.required]"
                      autocomplete="off"
                    />
                  </v-col>
                  <v-col v-if="(sourceType === 'pipeline' || sourceType === 'pipeline_primary_client' ) && selected.length" cols="12">
                    <v-select
                      v-model="pipelineRole"
                      :items="contactRoles"
                      label="Contact Role"
                      :rules="[rules.required]"
                      autocomplete="off"
                    />
                  </v-col>
                  <v-col v-if="sourceType === 'occupier' && selected.length" cols="12">
                    <v-select
                      v-model="role"
                      :items="contactRoles"
                      label="Contact Role"
                      :rules="[rules.required]"
                      autocomplete="off"
                    />
                  </v-col>
                </v-row>
                <v-row v-if="hasKeyword && !contacts.length && !loadingContacts">
                  <v-col cols="12" class="text-center">
                    No results found for <strong>{{ contactKeyword }}</strong>
                  </v-col>
                  <v-col v-if="!isContributorSource" cols="12" class="text-center">
                    <v-btn data-track="contact.wizard.create" @click="e1 = 2">Create New Contact</v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-container>
            <!-- Only once a contact is picked: an empty bar left a blank band under the search. -->
            <v-card-actions v-if="selected.length" class="sc-navigation-drawer-actions">
              <v-spacer />
              <v-btn
                v-if="sourceType === 'property'"
                color="primary"
                :loading="saving"
                data-track="contact.property.attach"
                @click="addToProperty"
              >
                Save to Property
              </v-btn>
              <v-btn
                v-else-if="sourceType === 'company'"
                color="primary"
                :loading="saving"
                data-track="contact.company.attach"
                @click="addToCompany"
              >
                Save to Company
              </v-btn>
              <v-btn
                v-else-if="sourceType === 'availability'"
                color="primary"
                :loading="saving"
                data-track="contact.availability.attach"
                @click="addToListing"
              >
                Save to Availability
              </v-btn>
              <v-btn
                v-else-if="sourceType === 'pipeline' || sourceType === 'pipeline_primary_client'"
                color="primary"
                :loading="saving"
                data-track="contact.pipeline.attach"
                @click="addToPipeline"
              >
                Save to Pipeline
              </v-btn>
              <v-btn
                v-else-if="sourceType === 'occupier'"
                color="primary"
                :loading="saving"
                data-track="contact.occupier.attach"
                @click="addToOccupier"
              >
                Save to Occupier
              </v-btn>
              <v-btn
                v-else-if="sourceType === 'comparable'"
                color="primary"
                :loading="saving"
                data-track="contact.comparable.attach"
                @click="addToComparable"
              >
                Save to Comparable
              </v-btn>
              <v-btn
                v-else-if="sourceType === 'requirement' || isContributorSource"
                color="primary"
                :loading="saving"
                data-track="contact.requirement.attach"
                @click="addToRequirement"
              >
                {{ isContributorSource ? 'Save as Contributor' : 'Save to Requirement' }}
              </v-btn>
              <v-btn
                v-else
                color="primary"
                :loading="saving"
                data-track="contact.wizard.save"
                @click="saveContactHandler"
              >
                Save <span class="d-none d-sm-flex">&nbsp;Contact</span>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-window-item>
        <v-stepper-window-item :value="stepTwo">
          <v-card v-if="sourceType !== 'company'" variant="flat" class="pa-4">
            <v-form ref="companyForm">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="companyKeyword"
                    label="Search by company name or url"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    :loading="loadingCompanies"
                    hint="At least 3 characters"
                    :rules="[rules.min]"
                    autocomplete="off"
                  />
                </v-col>
              </v-row>
              <v-row v-if="hasCompanyKeyword && loadingCompanies" class="mt-0">
                <v-col cols="12" class="pt-3">
                  <v-card variant="outlined" data-test="company-search-skeleton">
                    <v-skeleton-loader type="list-item-avatar-two-line@5" />
                  </v-card>
                </v-col>
              </v-row>
              <v-row v-if="companies.length && hasCompanyKeyword && !loadingCompanies" class="mt-0">
                <v-col cols="12" class="pt-3">
                  <div class="text-body-2 text-medium-emphasis mb-2">
                    Results ({{ companies.length }} companies)
                  </div>
                  <v-card variant="outlined">
                    <v-list lines="two" class="py-0">
                      <template v-for="(company, index) in paginatedCompanies" :key="company._key">
                        <v-divider v-if="index" />
                        <v-list-item
                          :active="selectedCompany?._key === company._key"
                          color="primary"
                        >
                          <template #prepend>
                            <v-avatar>
                              <img :src="company.logo || $placeholderCompanyImg" alt="">
                            </v-avatar>
                          </template>
                          <div>
                            <v-list-item-title>
                              {{ company.name }}
                            </v-list-item-title>
                            <v-list-item-subtitle>
                              {{ company.address.street }}
                            </v-list-item-subtitle>
                          </div>
                          <template #append>
                            <v-btn
                              color="primary"
                              variant="tonal"
                              size="small"
                              @click="addContactWithCompany(company)"
                            >
                              Select
                            </v-btn>
                          </template>
                        </v-list-item>
                      </template>
                    </v-list>
                    <template v-if="companiesPaginationLength > 1">
                      <v-divider />
                      <v-pagination
                        v-model="currentCompaniesPage"
                        :length="companiesPaginationLength"
                        :total-visible="paginationTotalVisible"
                        density="comfortable"
                        size="small"
                        class="py-1"
                      />
                    </template>
                  </v-card>
                </v-col>
              </v-row>
              <v-row v-if="hasCompanyKeyword && !companies.length && !loadingCompanies ">
                <v-col cols="12" class="text-center">
                  No results found for <strong>{{ companyKeyword }}</strong>
                </v-col>
                <v-col cols="12" class="text-center">
                  <v-btn data-track="contact.wizard-company.create" @click="companyDialog = true">Create New Company</v-btn>
                </v-col>
              </v-row>
              <v-row v-else-if="hasCompanyKeyword && !loadingCompanies">
                <v-col cols="12" class="text-center">
                  Not finding the company you are looking for?
                  <a data-track="contact.wizard-company.create" @click="companyDialog = true">Create a new one.</a>
                </v-col>
              </v-row>
              <v-card-actions class="sc-navigation-drawer-actions">
                <v-spacer />
                <v-btn color="primary" data-track="contact.wizard.skip" @click="skipAddContact">Skip</v-btn>
                <div v-if="selectedCompany">
                  <v-btn
                    v-if="sourceType !== 'company'"
                    color="primary"
                    data-track="contact.wizard.next"
                    @click="addContactWithCompany(selectedCompany)"
                  >
                    Next
                  </v-btn>
                </div>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-stepper-window-item>

        <!-- CONTACT FORM -->
        <v-stepper-window-item :value="stepThree" class="px-0">
          <v-card flat>
            <v-form ref="contactForm">
              <v-container v-if="e1 === stepThree" grid-list-lg class="px-4">
                <v-row>
                  <v-col cols="12" class="d-flex align-center justify-space-between">
                    <ImportFromVCard class="d-inline-block mr-3" @parsingFinished="onVCardParsingFinished" />
                    <Upload
                      v-model="uploadPhotoDialog"
                      :options="uploadOptions"
                      source-type="contacts"
                      @fileUploaded="uploadSuccess"
                    >
                      <v-btn data-track="contact.wizard-photo.upload" @click="uploadPhotoDialog = true">Upload Photo</v-btn>
                    </Upload>
                  </v-col>
                  <v-col cols="12" sm="6" class="d-flex align-center">
                    <v-img
                      :src="contact.img || contactPlaceholder"
                      width="200"
                      height="200"
                      max-width="200"
                      alt="contact photo"
                      :cover="false"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" align-self="center">
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="contact.first_name"
                          label="First Name"
                          :rules="defaultRules"
                          required
                          autocomplete="off"
                          hide-details="auto"
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-text-field
                          v-model="contact.last_name"
                          label="Last Name"
                          :rules="defaultRules"
                          required
                          autocomplete="off"
                          hide-details="auto"
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-text-field
                          v-model="contact.nickname"
                          label="Nickname"
                          autocomplete="off"
                          hide-details
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                  <!-- One row, so only the column gutters space the fields: separate
                       rows' -12px margins collapsed and left 12px under the photo. -->
                  <v-col v-if="contactCompany" cols="12">
                    <h3 class="ma-0 d-flex align-center ga-2">
                      <v-avatar>
                        <img :src="contactCompany.logo" alt="">
                      </v-avatar>
                      {{ contactCompany.name }}
                    </h3>
                  </v-col>
                  <v-col v-if="source === 'company'" cols="12">
                    <!-- eslint-disable -->
                    <v-text-field
                      v-model="source.name"
                      label="Company Name"
                      autocomplete="off"
                      disabled
                    />
                    <!-- eslint-enable -->
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="contact.title"
                      label="Title"
                      autocomplete="off"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <CommonPhonePicker
                      show-extension
                      :phone-country-code="contact.phone.direct_country_code"
                      :phone-number="contact.phone.direct"
                      :phone-extension="contact.phone.direct_ext"
                      @save:number="contact.phone.direct = $event"
                      @save:countryCode="contact.phone.direct_country_code = $event"
                      @save:extension="contact.phone.direct_ext = $event"
                    >
                      <template #default="{ props }">
                        <v-text-field
                          v-model="contact.phone.direct"
                          v-facade="getCountryMask(contact.phone.direct_country_code)"
                          label="Direct Phone"
                          :suffix="contact.phone.direct_ext ? `ext: ${contact.phone.direct_ext}` : null"
                          v-bind="props"
                        />
                      </template>
                    </CommonPhonePicker>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <CommonPhonePicker
                      show-extension
                      :phone-country-code="contact.phone.mobile_country_code"
                      :phone-number="contact.phone.mobile"
                      :phone-extension="contact.phone.mobile_ext"
                      @save:number="contact.phone.mobile = $event"
                      @save:countryCode="contact.phone.mobile_country_code = $event"
                      @save:extension="contact.phone.mobile_ext = $event"
                    >
                      <template #default="{ props }">
                        <v-text-field
                          v-model="contact.phone.mobile"
                          v-facade="getCountryMask(contact.phone.mobile_country_code)"
                          label="Mobile Phone"
                          :suffix="contact.phone.mobile_ext ? `ext: ${contact.phone.mobile_ext}` : contact.phone.mobile_ext"
                          v-bind="props"
                        />
                      </template>
                    </CommonPhonePicker>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <CommonPhonePicker
                      show-extension
                      :phone-country-code="contact.phone.office_country_code"
                      :phone-number="contact.phone.office"
                      :phone-extension="contact.phone.office_ext"
                      @save:number="contact.phone.office = $event"
                      @save:countryCode="contact.phone.office_country_code = $event"
                      @save:extension="contact.phone.office_ext = $event"
                    >
                      <template #default="{ props }">
                        <v-text-field
                          v-model="contact.phone.office"
                          v-facade="getCountryMask(contact.phone.office_country_code)"
                          :disabled="contact.office_address_same_as_company"
                          label="Office Phone"
                          :suffix="contact.phone.office_ext ? `ext: ${contact.phone.office_ext}` : contact.phone.office_ext"
                          v-bind="props"
                        />
                      </template>
                    </CommonPhonePicker>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <CommonPhonePicker
                      show-extension
                      :phone-country-code="contact.phone.fax_country_code"
                      :phone-number="contact.phone.fax"
                      :phone-extension="contact.phone.fax_ext"
                      @save:number="contact.phone.fax = $event"
                      @save:countryCode="contact.phone.fax_country_code = $event"
                      @save:extension="contact.phone.fax_ext = $event"
                    >
                      <template #default="{ props }">
                        <v-text-field
                          v-model="contact.phone.fax"
                          v-facade="getCountryMask(contact.phone.fax_country_code)"
                          label="Fax"
                          v-bind="props"
                          :suffix="contact.phone.fax_ext ? `ext: ${contact.phone.fax_ext}` : contact.phone.fax_ext"
                          :disabled="contact.office_address_same_as_company"
                        />
                      </template>
                    </CommonPhonePicker>
                  </v-col>

                  <v-col cols="12">
                    <v-text-field
                      ref="contactEmail"
                      v-model="contact.email"
                      label="Email Address"
                      autocomplete="off"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="contact.linkedin_url"
                      label="LinkedIn"
                      :rules="[rules.linkedIdUrl]"
                      autocomplete="off"
                    />
                  </v-col>
                  <v-col cols="12" class="contact-label-container">
                    <v-label>Office Address</v-label>
                  </v-col>
                  <v-col cols="12">
                    <v-checkbox
                      v-model="contact.office_address_same_as_company"
                      label="Office address same as company address"
                      :disabled="!contactCompany"
                    />
                  </v-col>
                  <v-col cols="12">
                    <CommonAddressWidget
                      v-model="contact.office_address"
                      show-suite
                      source="contact"
                      class="pa-0"
                      :disabled="contact.office_address_same_as_company"
                    />
                  </v-col>
                  <v-col cols="12" class="contact-label-container">
                    <v-label>Home Contact Information</v-label>
                  </v-col>
                  <v-col cols="12">
                    <CommonAddressWidget
                      v-model="contact.home_address"
                      show-suite
                      source="contact"
                      class="pa-0"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <CommonPhonePicker
                      :phone-number="contact.home_phone"
                      @save:number="contact.home_phone = $event"
                    >
                      <template #default="{ props }">
                        <v-text-field
                          v-model="contact.home_phone"
                          v-facade="getCountryMask(null)"
                          label="Home Phone"
                          v-bind="props"
                        />
                      </template>
                    </CommonPhonePicker>
                  </v-col>
                  <!-- Only with roles to pick: from the contacts list there are none, and
                       the empty select showed the role set when a company was picked. -->
                  <v-col v-if="isShowContactRolesContactSelect && contactRoles.length" cols="12">
                    <v-divider class="mb-4" />
                    <v-select
                      v-model="role"
                      :items="contactRoles"
                      label="Contact Role"
                      :rules="[rules.required]"
                      autocomplete="off"
                    />
                  </v-col>
                  <v-col cols="12" class="pa-0">
                    <AppPrivacy v-model="contact.permissions" />
                  </v-col>
                </v-row>
              </v-container>

              <v-card-actions class="sc-navigation-drawer-actions">
                <v-btn :size="$vuetify.display.smAndDown ? 'small' : undefined" variant="text" @click="goBack">Back</v-btn>
                <v-spacer />
                <v-btn :size="$vuetify.display.smAndDown ? 'small' : undefined" variant="text" @click="cancelContact">Cancel</v-btn>
                <v-btn
                  :size="$vuetify.display.smAndDown ? 'small' : undefined"
                  color="primary"
                  :loading="saving"
                  class="px-4"
                  data-track="contact.wizard.save"
                  @click="saveContactHandler"
                >
                  Save
                  <span v-if="sourceType !== 'newContact' && sourceType !== 'ContactSelect'" class="d-none d-sm-flex">
                    <!-- A flex container drops plain spaces between its items. -->
                    &nbsp;to&nbsp;<span class="text-uppercase">{{ saveTargetLabel }}</span>
                  </span>
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>
    <CommonSidePanel v-model="duplicateContactDialog">
      <v-card flat>
        <v-toolbar class="panel-header mb-3 text-white">
          <v-toolbar-title>We found a match</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <div class="mb-5">The email address, {{ dupContact.email }}, already exists</div>
          <ContactCard :item="dupContact" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" class="cancel" @click="cancelDuplicateContact">Cancel</v-btn>
          <v-btn
            class="primary"
            :loading="contactAddLoading"
            data-track="contact.wizard-duplicate.add"
            @click="handleDuplicateFormSubmit(dupContact)"
          >
            Add Contact
          </v-btn>
        </v-card-actions>
      </v-card>
    </CommonSidePanel>
    <CommonSidePanel v-model="companyDialog">
      <v-card>
        <CompanyForm
          v-if="companyDialog"
          source-type="newContact"
          @companyAdded="useCompany"
          @closeDialog="closeCompany"
          @cancelCompany="closeCompany"
        />
      </v-card>
    </CommonSidePanel>
  </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { debounce } from 'lodash-es';

import { ServiceFactory } from '@/services/ServiceFactory';
import { ContactRolesEnum } from '@/bundles/Contact/enums/contactRoles';
import { getDefaultContact } from '@/bundles/Contact/helpers/getDefaultContact';
import { DefaultPermissionsMixin } from '@/bundles/App/mixins/DefaultPermissionsMixin';
import {
  getContactRolesBySource,
  sourceMappingDictionary,
} from '@/bundles/Contact/helpers/cards/contactsCard';
import { AppFormMixin } from '@/bundles/App/mixins/forms/AppFormMixin';
import { CountriesMasksMixin } from '@/bundles/Common/mixins/CountriesMasksMixin';
import { RequestAbortMixin } from '@/bundles/App/mixins/RequestAbortMixin';
import { isRequestCancelledError } from '@/bundles/Http/mappers/ErrorMapper';
import { MIN_CHAR_COUNT } from '@/bundles/App/consts';
import { validationRules } from '@/utils/validation.ts';
import { useNotifications } from '@/bundles/App/composables/useNotifications';
import { usePlaceholders } from '@/bundles/App/composables/usePlaceholders';
import { trackActionSuccess } from '@/bundles/Analytics/helpers/gtm';
import { KeyActionEnum } from '@/bundles/Analytics/types';

import ImportFromVCard from '@/components/common-components/ImportFromVCard';
import ContactCard from '@/components/common-components/recordCards/ContactCard';
import ClientAccessChip from '@/bundles/Contact/components/chips/ClientAccessChip.vue';
import AppPrivacy from '@/bundles/App/components/AppPrivacy';
import Upload from '@/bundles/Upload/components/Upload';
import CommonPhonePicker from '@/bundles/Common/components/CommonPhonePicker.vue';
import CommonAddressWidget from '@/components/common-components/address/CommonAddressWidget.vue';
import CommonSidePanel from '@/bundles/Common/components/panels/CommonSidePanel.vue';

import CompanyForm from '../companies/CompanyForm';

const ContactService = ServiceFactory.get('contact');
const CompanyService = ServiceFactory.get('company');
const PropertyService = ServiceFactory.get('property');
const AvailabilityService = ServiceFactory.get('availability');
const OccupierService = ServiceFactory.get('occupier');
const RequirementService = ServiceFactory.get('requirement');
const PipelineService = ServiceFactory.get('pipeline');
const ComparableService = ServiceFactory.get('comparable');

const getRecordServiceBySourceType = (sourceType) => {
  const sourceTypesSharingRecordService = ['pipeline_primary_client', 'requirement_contributor'];
  const sourceTypeMapped = sourceTypesSharingRecordService.includes(sourceType) ? sourceMappingDictionary[sourceType] : sourceType;

  return ServiceFactory.get(sourceTypeMapped);
}

export default {
  name: 'ContactAddStepper',

  components: {
    CommonSidePanel,
    CommonAddressWidget,
    CommonPhonePicker,
    Upload,
    CompanyForm,
    ImportFromVCard,
    ContactCard,
    AppPrivacy,
    ClientAccessChip
  },

  mixins: [DefaultPermissionsMixin, AppFormMixin, CountriesMasksMixin, RequestAbortMixin],

  props: {
    source: {
      type: Object,
      default: () => ({}),
    },
    sourceType: {
      type: String,
      default: 'newContact',
    },
    sourceId: {
      type: [String, Number],
      default: '',
    },
    hasRoles: {
      type: Boolean,
      default: true
    },
    // the role the stepper starts with (a role card's "+" on a record's Contacts card)
    presetRole: {
      type: String,
      default: null,
    },
  },

  emits: ['cancelContact', 'contactAdded'],

  setup () {
    const { addNotification } = useNotifications();
    const { contactPlaceholder } = usePlaceholders();

    return { addNotification, contactPlaceholder };
  },

  data: () => ({
    contact: getDefaultContact(),
    companyDialog: false,
    e1: 1,
    loadingContacts: false,
    contactKeyword: '',
    contacts: [],
    selected: [],
    loadingCompanies: false,
    contactCompany: '',
    companies: [],
    companyKeyword: '',
    selectedCompany: '',
    pipelineRole: ContactRolesEnum.ultimate_decision_maker,
    rules: {
      required: validationRules.getRequiredRule(),
      min: validationRules.getMinRule(MIN_CHAR_COUNT),
      linkedIdUrl: validationRules.getLinkedIdUrl(),
    },
    duplicateContactDialog: false,
    dupContact: {},
    defaultRules: [(v) => !!v || 'Item is required'],
    saving: false,
    roleAlert: false,
    roleMessage: '',
    currentContactsPage: 1,
    currentCompaniesPage: 1,
    paginationSize: 5,
    paginationTotalVisible: 7,
    role: null,
    uploadPhotoDialog: false,
    sourceRecordService: null,
    contactAddLoading: false,
  }),

  computed: {
    ...mapState({
      token: (state) => state.idToken,
    }),
    ...mapGetters('Admin', {
      getListByCategory: 'getListByCategory'
    }),
    isShowContactRoles: function () {
      const arr = ['property', 'newContact', 'availability', 'company', 'comparable', 'requirement'];

      return arr.some(type => type === this.sourceType) && this.selected.length;
    },
    isShowContactRolesContactSelect: function () {
      const arr = ['property', 'availability', 'company', 'newContact', 'occupier', 'comparable', 'pipeline', 'pipeline_primary_client', 'requirement'];

      return arr.some(type => type === this.sourceType);
    },
    contactRoles () {
      return getContactRolesBySource(this.sourceType);
    },
    // SCBT-59: the requirement "Contributor" card only links existing contacts of active members
    // with a fixed contributor role, so contact creation and role selection are hidden.
    isContributorSource () {
      return this.sourceType === 'requirement_contributor';
    },
    saveTargetLabel () {
      const labels = {
        pipeline_primary_client: 'pipeline primary contacts',
        requirement_contributor: 'requirement contributors',
      };

      return labels[this.sourceType] || this.sourceType;
    },
    stepTwo () {
      if (
        this.sourceType === 'newContact' ||
        this.sourceType === 'ContactSelect'
      ) {
        return 1;
      } else {
        return 2;
      }
    },
    stepThree () {
      if (
        this.sourceType === 'newContact' ||
        this.sourceType === 'company' ||
        this.sourceType === 'ContactSelect'
      ) {
        return 2;
      } else {
        return 3;
      }
    },
    hasKeyword () {
      return this.contactKeyword?.length >= MIN_CHAR_COUNT;
    },
    hasCompanyKeyword () {
      return this.companyKeyword?.length >= MIN_CHAR_COUNT;
    },
    officePhone: {
      get () {
        if (this.contact.phone.office !== '') {
          return this.contact.phone.office;
        } else if (this.sourceType === 'company') {
          this.setOfficePhone(this.source.phone.office);
          return this.source.phone.office;
        } else if (this.sourceType === 'property' && this.contactCompany._key) {
          this.setOfficePhone(this.contactCompany.phone.office);
          return this.contactCompany.phone.office;
        }
        return '';
      },
      set (value) {
        this.contact.phone.office = value;
      },
    },
    contactsPaginationLength () {
      return Math.ceil(this.contacts.length / this.paginationSize);
    },
    companiesPaginationLength () {
      return Math.ceil(this.companies.length / this.paginationSize);
    },
    paginatedContacts () {
      const startPaginationIndex = (this.currentContactsPage - 1) * this.paginationSize;
      const endPaginationIndex = this.currentContactsPage * this.paginationSize;
      return this.contacts.slice(startPaginationIndex, endPaginationIndex);
    },
    paginatedCompanies () {
      const startPaginationIndex = (this.currentCompaniesPage - 1) * this.paginationSize;
      const endPaginationIndex = this.currentCompaniesPage * this.paginationSize;
      return this.companies.slice(startPaginationIndex, endPaginationIndex);
    },
    uploadOptions () {
      return { accept: 'image/*', aspectRatio: 1 };
    },
    selectedRole () {
      return this.role ? [this.role] : [];
    },
  },

  watch: {
    dupContact (newValue) {
      if (newValue._key) {
        this.duplicateContactDialog = true;
      }
    },
    contactKeyword (value) {
      if (value?.length >= MIN_CHAR_COUNT) {
        this.searchHandlerDebounced(this, 'searchContacts');
        return;
      }

      this.selected = [];
    },
    companyKeyword (value) {
      if (value?.length >= MIN_CHAR_COUNT) {
        this.searchHandlerDebounced(this, 'searchCompanies');
      }
    },
    selectedCompany (value) {
      if (value === null) {
        this.selectedCompany = {};
      }
    },
    sourceType: {
      immediate: true,
      handler (newValue) {
        this.checkType();
        this.setDefaultPipelineRole();

        if (newValue === 'ContactSelect' || newValue === 'newContact') {
          return;
        }

        this.sourceRecordService = getRecordServiceBySourceType(newValue);
      }
    },
    'contact.office_address_same_as_company' (newVal) {
      if (newVal && this.contactCompany) {
        const { city, postal, state, street, street2, timezone_name, country } = this.contactCompany.address;
        const { office, office_country_code, fax, fax_country_code } = this.contactCompany.phone;

        this.contact.office_address = { city, postal, state, street, street2, country, timezone_name };
        this.contact.phone = {
          ...this.contact.phone,
          office,
          office_country_code,
          fax,
          fax_country_code,
        };
      }
    },
    source: {
      handler (newVal) {
        this.contactCompany = newVal;
      },
      deep: true,
    },
    'contactCompany' (value) {
      if (value && this.sourceType !== 'pipeline_primary_client') {
        this.role = value.isBrokerage ? ContactRolesEnum.agent : ContactRolesEnum.employee;
      }
    },
  },

  beforeUnmount () {
    this.handleRequestAbort();
  },

  mounted () {
    this.setDefaultRole();
    this.contact.permissions = this.defaultPermissions;

    setTimeout(() => { // nextTick doesn't work here
      this.$refs.searchField?.focus();
    }, 100);
  },

  created () {
    if (this.sourceType === 'company') {
      this.setCompany();
    }
  },

  methods: {
    // Primary for the current and completed steps, Vuetify's grey otherwise
    // (v2's look). A stepper-wide colour would paint every step, because
    // Vuetify colours any editable step's disc.
    stepColor (value, complete) {
      return value === this.e1 || complete ? 'primary' : undefined;
    },
    searchHandlerDebounced: debounce(function (vm, eventName) {
      vm[eventName]();
    }, 400),
    cancelDuplicateContact () {
      this.duplicateContactDialog = false;
      this.dupContact = {};
      this.contact.email = '';
      this.$refs.contactEmail.focus();
    },
    goBack () {
      this.selectedCompany = '';
      this.e1 = 1;
      this.contactCompany = '';
    },
    closeCompany () {
      this.companyDialog = false;
    },
    useCompany (value) {
      this.companyDialog = false;
      this.addContactWithCompany(value);
    },
    setOfficePhone (value) {
      this.contact.phone.office = value;
    },
    handleDuplicateFormSubmit: async function (contact) {
      if (!contact || !this.sourceRecordService) {
        return;
      }

      try {
        this.contactAddLoading = true;
        await this.sourceRecordService.addContact({
          payload: {
            contacts: [contact._key],
            roles: this.selectedRole,
          },
          token: this.token,
          id: this.sourceId,
        });

        this.$emit('contactAdded', contact);
        this.resetContactForm();
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.contactAddLoading = false;
      }
    },
    saveContactHandler: async function () {
      const { valid } = await this.$refs.contactForm.validate();

      if (!valid) {
        this.showFormValidationErrorNotification();
        return;
      }

      this.saving = true;

      try {
        const submitDate = Date.now();
        const payload = {
          date_insert: submitDate,
          date_update: submitDate,
          first_name: this.contact.first_name,
          last_name: this.contact.last_name,
          nickname: this.contact.nickname,
          email: this.contact.email,
          title: this.contact.title,
          img: this.contact.img,
          permissions: this.contact.permissions,
          company: this.selectedCompany._key,
          phone: { ...this.contact.phone },
          home_address: this.contact.home_address,
          office_address: this.contact.office_address,
          home_phone: this.contact.home_phone,
          office_address_same_as_company: this.contact.office_address_same_as_company,
          linkedin_url: this.contact.linkedin_url,
        };

        const shouldUseSelectedRole = this.sourceType === 'company' || this.sourceType === 'newContact' || this.sourceType === 'ContactSelect';

        payload.roles = this.selectedRole.length && shouldUseSelectedRole ? this.selectedRole : [ContactRolesEnum.employee];

        const { data } = await ContactService.create({
          payload,
          token: this.token,
        });

        if (data.duplicate === 'email' && this.sourceType === 'newContact') {
          this.addNotification({
            title: 'Duplicate Contact',
            message: 'A contact with this email address already exists',
            status: 0,
          });
          return;
        }

        // the API answers a duplicate with the existing contact instead of creating one
        if (!data.duplicate) {
          trackActionSuccess(KeyActionEnum.ContactCreate);
        }

        if (this.sourceType === 'newContact' || this.sourceType === 'ContactSelect') {
          this.$emit('contactAdded', data);
          this.cancelContact();
          return;
        }

        if (data.duplicate) {
          this.dupContact = data.contact;
          return;
        }

        if (!this.sourceRecordService) {
          return;
        }

        await this.sourceRecordService.addContact({
          payload: {
            contacts: [data._key],
            roles: this.selectedRole,
          },
          token: this.token,
          id: this.sourceId,
        });
        this.$emit('contactAdded', data);
        this.resetContactForm();
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.saving = false;
      }
    },
    cancelContact () {
      this.$emit('cancelContact');
      this.resetContactForm();
    },
    addContactWithCompany (company) {
      this.contactCompany = company;
      this.selectedCompany = company;
      this.e1 = this.stepThree;
      this.contact.office_address_same_as_company = true;
    },
    skipAddContact: function () {
      this.e1 = this.stepThree;
    },
    searchContacts: async function () {
      this.currentContactsPage = 1;
      try {
        this.loadingContacts = true;
        this.handleRequestAbort();

        const params = [
          { name: 'keyword', value: this.contactKeyword },
          { name: 'pageOffset', value: 0 },
          { name: 'pageLimit', value: 50 },
          { name: 'sort', value: 'last_name' },
        ];

        if (this.isContributorSource) {
          // Only contacts linked to a member; an archived member is unlinked from its contact.
          params.push({ name: 'contact_internal', value: true });
        }

        const { data } = await ContactService.list({ token: this.token, params, controller: this.abortController });

        this.contacts = data.result;
        this.loadingContacts = false;
      } catch (error) {
        this.addNotification(error);
        if (!isRequestCancelledError(error)) {
          this.loadingContacts = false; // can't be inside finally because fetchWrapper calls several times when changing table options
        }
      }
    },
    uploadSuccess ({ files }) {
      this.contact.img = files[0].url;
    },
    async searchCompanies () {
      this.currentCompaniesPage = 1;
      try {
        this.loadingCompanies = true;
        const params = [
          { name: 'keyword', value: this.companyKeyword },
          { name: 'pageLimit', value: 50 }
        ];

        const { data } = await CompanyService.list({ token: this.token, params });
        const { count, result: companies } = data;

        this.count = count;
        this.companies = companies;
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.loadingCompanies = false;
      }
    },
    setCompany () {
      this.contactCompany = this.source;
      this.contact.office_address_same_as_company = true;
    },
    async addToCompany () {
      const { valid } = await this.$refs.form.validate();

      if (!valid) {
        this.showFormValidationErrorNotification();
        return;
      }

      const payload = {
        contacts: this.selected,
        roles: this.selectedRole,
      };

      try {
        this.saving = true;

        const { data } = await CompanyService.addContact({
          payload,
          token: this.token,
          id: this.sourceId,
        });
        this.$refs.form.reset();
        this.$emit('contactAdded');
        this.e1 = 1;
        this.contactKeyword = '';
        this.contacts = [];

        if (data?.failed?.length) {
          const message = `Contacts with key(s) ${data.failed.map(item => item.contact_key).join(', ')} already assigned to another company.`;

          this.addNotification({
            message,
            status: 0,
            title: 'Already connected to another company.'
          });
        }

        return data;
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.saving = false;
      }
    },
    async addToPipeline () {
      const { valid } = await this.$refs.form.validate();

      if (!valid) {
        this.showFormValidationErrorNotification();
        return;
      }

      const payload = {
        contacts: this.selected,
        roles: [this.pipelineRole],
      };

      try {
        this.saving = true;

        await PipelineService.addContact({
          id: this.sourceId,
          payload,
          token: this.token,
        });

        this.$emit('contactAdded');
        this.$refs.form.reset();
        this.e1 = 1;
        this.contactKeyword = '';
        this.contacts = [];
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.saving = false;
      }
    },
    async addToProperty () {
      const { valid } = await this.$refs.form.validate();

      if (!valid) {
        this.showFormValidationErrorNotification();
        return;
      }

      const payload = {
        contacts: this.selected,
        roles: this.selectedRole,
      };

      try {
        this.saving = true;
        await PropertyService.addContact({
          id: this.sourceId,
          payload,
          token: this.token,
        });
        this.resetContactForm();
        this.$emit('contactAdded');
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.saving = false;
      }
    },
    async addToListing () {
      const { valid } = await this.$refs.form.validate();

      if (!valid) {
        this.showFormValidationErrorNotification();
        return;
      }

      const payload = {
        contacts: this.selected,
        roles: this.selectedRole,
      };

      try {
        this.saving = true;

        await AvailabilityService.addContact({
          id: this.sourceId,
          payload,
          token: this.token,
        });
        this.e1 = 1;
        this.contactKeyword = '';
        this.contacts = [];
        this.$emit('contactAdded');
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.saving = false;
      }
    },

    async addToOccupier () {
      if (this.role === ContactRolesEnum.employee) {
        this.roleAlert = true;
        this.roleMessage = 'Please select a role';
      }

      const { valid } = await this.$refs.form.validate();

      if (!valid) {
        this.showFormValidationErrorNotification();
        return;
      }

      if (this.role !== ContactRolesEnum.employee) {
        const payload = {
          contacts: this.selected,
          roles: this.selectedRole,
        };

        try {
          this.saving = true;

          await OccupierService.addContact({
            id: this.sourceId,
            token: this.token,
            payload,
          });
          this.e1 = 1;
          this.contactKeyword = '';
          this.contacts = [];
          this.$emit('contactAdded');
        } catch (error) {
          this.addNotification({ ...error });
        } finally {
          this.saving = false;
        }
      }
    },
    async addToComparable () {
      const { valid } = await this.$refs.form.validate();

      if (!valid) {
        this.showFormValidationErrorNotification();
        return;
      }

      const payload = {
        contacts: this.selected,
        role: this.selectedRole[0],
      };

      try {
        this.saving = true;

        await ComparableService.addContact({
          id: this.sourceId,
          payload,
          token: this.token,
        });

        this.$refs.form.reset();
        this.$emit('contactAdded');
        this.e1 = 1;
        this.contactKeyword = '';
        this.contacts = [];
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.saving = false;
      }
    },
    async addToRequirement () {
      const { valid } = await this.$refs.form.validate();

      if (!valid) {
        this.showFormValidationErrorNotification();
        return;
      }

      this.saving = true;

      try {
        await RequirementService.addContact({
          payload: {
            contacts: this.selected,
            roles: this.isContributorSource ? [ContactRolesEnum.contributor] : this.selectedRole,
          },
          token: this.token,
          id: this.sourceId,
        });

        this.$refs.form.reset();
        this.$emit('contactAdded');
        this.e1 = 1;
        this.contactKeyword = '';
        this.contacts = [];
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.saving = false;
      }
    },
    checkType () {
      if (this.sourceType === 'newContact' && this.e1 === 1) {
        this.e1 = 2;
      }
    },
    setDefaultPipelineRole () {
      this.pipelineRole = this.presetRole || (this.sourceType === 'pipeline_primary_client' ? ContactRolesEnum.ultimate_decision_maker : ContactRolesEnum.seller_broker);
    },
    resetContactForm () {
      this.e1 = 1;
      this.contactKeyword = '';
      this.companyKeyword = '';
      this.selectedCompany = '';
      this.contactCompany = '';
      this.companies = [];
      this.selected = [];
      this.contacts = [];
      this.setDefaultRole();
      this.setDefaultPipelineRole();
      this.contact = getDefaultContact();
      this.contact.permissions = this.defaultPermissions;
    },
    onVCardParsingFinished (parsedData) {
      this.contact = Object.assign(this.contact, parsedData);
    },
    toggleContact: function (key) {
      const selectedIndex = this.selected.indexOf(key);

      selectedIndex === -1 ? this.selected.push(key) : this.selected.splice(selectedIndex, 1);
    },
    setDefaultRole: function () {
      if (this.presetRole) {
        this.role = this.presetRole;
        return;
      }

      switch (this.sourceType) {
        case 'property':
        case 'availability':
        case 'requirement':
          this.role = ContactRolesEnum.listing_agent;
          break;
        case 'occupier':
          this.role = ContactRolesEnum.procuring_broker;
          break;
        case 'company':
          this.role = this.source.isBrokerage ? ContactRolesEnum.agent : ContactRolesEnum.employee;
          break;
        case 'newContact':
          this.role = ContactRolesEnum.employee;
          break;
        default:
          this.role = '';
      };
    },
  },
};
</script>
